import React, { useState, useEffect, useCallback } from 'react';
import { customersAPI, plansAPI } from '../../services/api';
import { formatDate, formatCurrency } from '../../utils/helpers';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';
import Pagination from '../../components/common/Pagination';
import PermissionGuard from '../../components/common/PermissionGuard';
import Spinner from '../../components/common/Spinner';
import CustomerUsagePanel from './CustomerUsagePanel';
import toast from 'react-hot-toast';

const Customers = () => {
  const [customers, setCustomers]   = useState([]);
  const [plans, setPlans]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [pagination, setPagination] = useState(null);
  const [search, setSearch]         = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage]             = useState(1);
  const [showModal, setShowModal]   = useState(false);
  const [showAssignPlan, setShowAssignPlan] = useState(null);
  const [editCustomer, setEditCustomer] = useState(null);
  const [saving, setSaving]         = useState(false);
  const [form, setForm]             = useState({ name: '', email: '', company: '', phone: '', planId: '', notes: '' });

  // Customer detail/usage drawer
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [detailTab, setDetailTab]               = useState('info'); // 'info' | 'usage'

  // Reset password modal
  const [resetPwdCustomer, setResetPwdCustomer] = useState(null);
  const [resetMode, setResetMode]               = useState('auto'); // 'auto' | 'custom'
  const [resetPwd, setResetPwd]                 = useState('');
  const [resetting, setResetting]               = useState(false);
  const [generatedPwd, setGeneratedPwd]         = useState('');

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await customersAPI.getAll({ page, search, status: statusFilter, limit: 10 });
      setCustomers(res.data.data);
      setPagination(res.data.pagination);
    } catch { toast.error('Failed to load customers'); }
    finally { setLoading(false); }
  }, [page, search, statusFilter]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);
  useEffect(() => { plansAPI.getAll({ isActive: true }).then(r => setPlans(r.data.data)).catch(() => {}); }, []);

  const openCreate = () => { setEditCustomer(null); setForm({ name: '', email: '', company: '', phone: '', planId: '', notes: '' }); setShowModal(true); };
  const openEdit   = (c) => { setEditCustomer(c); setForm({ name: c.name, email: c.email, company: c.company || '', phone: c.phone || '', notes: c.notes || '', planId: '' }); setShowModal(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editCustomer) { await customersAPI.update(editCustomer._id, form); toast.success('Customer updated'); }
      else { await customersAPI.create(form); toast.success('Customer created! Credentials sent via email.'); }
      setShowModal(false);
      fetchCustomers();
    } catch (err) { toast.error(err.response?.data?.message || 'Operation failed'); }
    finally { setSaving(false); }
  };

  const handleBlock = async (c) => {
    try {
      if (c.status === 'blocked') { await customersAPI.unblock(c._id); toast.success('Customer unblocked'); }
      else { await customersAPI.block(c._id); toast.success('Customer blocked'); }
      fetchCustomers();
    } catch { toast.error('Operation failed'); }
  };

  const handleAssignPlan = async (planId) => {
    try {
      await customersAPI.assignPlan(showAssignPlan, planId);
      toast.success('Plan assigned');
      setShowAssignPlan(null);
      fetchCustomers();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to assign plan'); }
  };

  const openDetail = (c) => { setSelectedCustomer(c); setDetailTab('info'); };

  const openResetPwd = (c) => {
    setResetPwdCustomer(c);
    setResetMode('auto');
    setResetPwd('');
    setGeneratedPwd('');
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (resetMode === 'custom') {
      if (!resetPwd || resetPwd.length < 6) {
        toast.error('Password must be at least 6 characters');
        return;
      }
    }
    setResetting(true);
    try {
      const body = resetMode === 'custom' ? { password: resetPwd } : {};
      const res = await customersAPI.resetPassword(resetPwdCustomer._id, body);
      if (res.data?.tempPassword) {
        setGeneratedPwd(res.data.tempPassword);
        toast.success('Password reset — temporary password generated');
      } else {
        toast.success('Password reset successfully');
        setResetPwdCustomer(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Reset failed');
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="flex gap-4 h-full">
      {/* ── Main Table ── */}
      <div className={`flex-1 space-y-4 min-w-0 transition-all ${selectedCustomer ? 'max-w-[60%]' : ''}`}>
        {/* Toolbar */}
        <div className="flex items-center gap-3 flex-wrap">
          <input type="text" placeholder="Search customers..." value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
            <option value="">All Status</option>
            {['active', 'inactive', 'blocked', 'trial'].map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
          </select>
          <div className="ml-auto">
            <PermissionGuard module="customers" action="create">
              <button onClick={openCreate} className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition">
                + Add Merchant
              </button>
            </PermissionGuard>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? <div className="p-12"><Spinner /></div> : (
            <>
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left text-gray-500 text-xs uppercase tracking-wider">
                    <th className="px-5 py-3 font-medium">Merchant</th>
                    {!selectedCustomer && <th className="px-5 py-3 font-medium">Company</th>}
                    <th className="px-5 py-3 font-medium">Status</th>
                    <th className="px-5 py-3 font-medium">Plan</th>
                    {!selectedCustomer && <th className="px-5 py-3 font-medium">Joined</th>}
                    <th className="px-5 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {customers.map(c => (
                    <tr key={c._id}
                      onClick={() => openDetail(c)}
                      className={`hover:bg-gray-50 cursor-pointer ${selectedCustomer?._id === c._id ? 'bg-indigo-50' : ''}`}
                    >
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-700 font-bold flex-shrink-0">
                            {c.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium truncate">{c.name}</p>
                            <p className="text-xs text-gray-400 truncate">{c.email}</p>
                          </div>
                        </div>
                      </td>
                      {!selectedCustomer && <td className="px-5 py-3 text-gray-500">{c.company || '—'}</td>}
                      <td className="px-5 py-3"><Badge status={c.status} /></td>
                      <td className="px-5 py-3 text-xs text-gray-600">
                        {c.currentSubscription?.plan?.name || <span className="text-gray-300">—</span>}
                      </td>
                      {!selectedCustomer && <td className="px-5 py-3 text-gray-400 text-xs">{formatDate(c.createdAt)}</td>}
                      <td className="px-5 py-3">
                        <div className="flex gap-2 flex-wrap" onClick={e => e.stopPropagation()}>
                          <PermissionGuard module="customers" action="update">
                            <button onClick={() => openEdit(c)} className="text-indigo-600 hover:underline text-xs">Edit</button>
                            <button onClick={() => setShowAssignPlan(c._id)} className="text-blue-600 hover:underline text-xs">Plan</button>
                            <button onClick={() => openResetPwd(c)} className="text-amber-600 hover:underline text-xs">Reset Pwd</button>
                            <button onClick={() => handleBlock(c)} className={`${c.status === 'blocked' ? 'text-green-600' : 'text-red-600'} hover:underline text-xs`}>
                              {c.status === 'blocked' ? 'Unblock' : 'Block'}
                            </button>
                          </PermissionGuard>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {customers.length === 0 && <tr><td colSpan={6} className="px-5 py-12 text-center text-gray-400">No Merchant found</td></tr>}
                </tbody>
              </table>
              <Pagination pagination={pagination} onPageChange={setPage} />
            </>
          )}
        </div>
      </div>

      {/* ── Customer Detail Drawer ── */}
      {selectedCustomer && (
        <div className="w-80 xl:w-96 bg-white border border-gray-200 rounded-xl flex flex-col overflow-hidden flex-shrink-0">
          {/* Drawer header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-green-700 font-bold">
                {selectedCustomer.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="font-semibold text-gray-900">{selectedCustomer.name}</p>
                <p className="text-xs text-gray-400">{selectedCustomer.email}</p>
              </div>
            </div>
            <button onClick={() => setSelectedCustomer(null)} className="text-gray-400 hover:text-gray-600 text-lg leading-none">✕</button>
          </div>

          {/* Drawer tabs */}
          <div className="flex border-b border-gray-100">
            {['info', 'usage'].map(tab => (
              <button key={tab}
                onClick={() => setDetailTab(tab)}
                className={`flex-1 py-2.5 text-sm font-medium transition capitalize ${
                  detailTab === tab
                    ? 'text-indigo-600 border-b-2 border-indigo-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab === 'usage' ? '📊 Usage' : 'ℹ️ Info'}
              </button>
            ))}
          </div>

          {/* Drawer content */}
          <div className="flex-1 overflow-y-auto">
            {detailTab === 'info' && (
              <div className="p-4 space-y-3 text-sm">
                {/* Status */}
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Status</span>
                  <Badge status={selectedCustomer.status} />
                </div>
                {/* Plan */}
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Plan</span>
                  <span className="font-medium">
                    {selectedCustomer.currentSubscription?.plan?.name || '—'}
                  </span>
                </div>
                {/* Subscription period */}
                {selectedCustomer.currentSubscription && (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Period start</span>
                      <span className="text-xs">{formatDate(selectedCustomer.currentSubscription.startDate)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Period end</span>
                      <span className="text-xs">{formatDate(selectedCustomer.currentSubscription.endDate)}</span>
                    </div>
                  </>
                )}
                {/* Company / phone */}
                {selectedCustomer.company && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Company</span>
                    <span className="font-medium">{selectedCustomer.company}</span>
                  </div>
                )}
                {selectedCustomer.phone && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Phone</span>
                    <span>{selectedCustomer.phone}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">Joined</span>
                  <span className="text-xs">{formatDate(selectedCustomer.createdAt)}</span>
                </div>

                {/* Plan limits summary */}
                {selectedCustomer.currentSubscription?.plan && (() => {
                  const p = selectedCustomer.currentSubscription.plan;
                  const limits = [
                    // { icon: '🤖', label: 'AI Queries',      val: p.aicustmoersLimit        },
                    { icon: '👥', label: 'Customers',       val: p.customersLimit          },
                    { icon: '🧾', label: 'AI Invoice Scans',   val: p.receiptScanLimit     },
                    { icon: '📄', label: 'Manual Invoices', val: p.manualInvoiceLimit   },
                    // { icon: '⚡', label: 'API Tokens',      val: p.apiitemsLimit        },
                    { icon: '📦', label: 'Items',           val: p.itemsLimit           }
                  ];
                  return (
                    <div className="border border-gray-200 rounded-xl p-3 mt-2">
                      <p className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Plan Limits / Cycle</p>
                      <div className="grid grid-cols-2 gap-1.5">
                        {limits.map(l => (
                          <div key={l.label} className="bg-gray-50 rounded-lg p-2 flex items-center gap-1.5">
                            <span className="text-sm">{l.icon}</span>
                            <div>
                              <p className="text-xs text-gray-400">{l.label}</p>
                              <p className="text-xs font-bold text-gray-800">{l.val === -1 ? '∞' : l.val?.toLocaleString()}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Quick actions */}
                <div className="pt-2 border-t flex flex-col gap-2">
                  <button onClick={() => { setShowModal(true); openEdit(selectedCustomer); }}
                    className="w-full py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition">
                    ✏️ Edit Customer
                  </button>
                  <button onClick={() => setShowAssignPlan(selectedCustomer._id)}
                    className="w-full py-2 text-sm border border-blue-200 text-blue-600 rounded-lg hover:bg-blue-50 transition">
                    📦 Change Plan
                  </button>
                  <button onClick={() => openResetPwd(selectedCustomer)}
                    className="w-full py-2 text-sm border border-amber-200 text-amber-600 rounded-lg hover:bg-amber-50 transition">
                    🔑 Reset Password
                  </button>
                  <button onClick={() => handleBlock(selectedCustomer)}
                    className={`w-full py-2 text-sm rounded-lg transition border ${selectedCustomer.status === 'blocked' ? 'border-green-200 text-green-600 hover:bg-green-50' : 'border-red-200 text-red-600 hover:bg-red-50'}`}>
                    {selectedCustomer.status === 'blocked' ? '✅ Unblock Customer' : '🚫 Block Customer'}
                  </button>
                </div>
              </div>
            )}

            {detailTab === 'usage' && (
              <div className="p-4">
                <CustomerUsagePanel customerId={selectedCustomer._id} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Create / Edit Modal ── */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editCustomer ? 'Edit Customer' : 'Create Customer'} size="lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {[['name','Name','text',true],['email','Email','email',true],['company','Company','text',false],['phone','Phone','text',false]].map(([key,label,type,req]) => (
              <div key={key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">{label}{req && ' *'}</label>
                <input type={type} value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })} required={req}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
            ))}
          </div>
          {!editCustomer && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assign Plan</label>
              <select value={form.planId} onChange={e => setForm({ ...form, planId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option value="">No Plan</option>
                {plans.map(p => <option key={p._id} value={p._id}>{p.name} — {formatCurrency(p.price)}/{p.billingCycle}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
          {!editCustomer && (
            <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg text-sm text-blue-700">
              Temporary credentials will be auto-generated and emailed to the customer.
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-60">
              {saving ? 'Saving...' : (editCustomer ? 'Update' : 'Create Customer')}
            </button>
          </div>
        </form>
      </Modal>

      {/* ── Assign Plan Modal ── */}
      <Modal isOpen={!!showAssignPlan} onClose={() => setShowAssignPlan(null)} title="Assign Plan">
        <div className="space-y-3">
          {plans.map(plan => (
            <button key={plan._id} onClick={() => handleAssignPlan(plan._id)}
              className="w-full text-left p-4 border border-gray-200 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition">
              <div className="flex justify-between">
                <span className="font-medium">{plan.name}</span>
                <span className="text-indigo-600 font-semibold">{formatCurrency(plan.price)}/{plan.billingCycle}</span>
              </div>
              <div className="grid grid-cols-3 gap-1 mt-2 text-xs text-gray-500">
                <span>🤖 {plan.aicustmoersLimit === -1 ? '∞' : plan.aicustmoersLimit} AI</span>
                <span>👥 {plan.customersLimit === -1 ? '∞' : plan.customersLimit} cust.</span>
                <span>🧾 {plan.receiptScanLimit === -1 ? '∞' : plan.receiptScanLimit} scans</span>
                <span>📄 {plan.manualInvoiceLimit === -1 ? '∞' : plan.manualInvoiceLimit} inv.</span>
                <span>⚡ {plan.apiitemsLimit === -1 ? '∞' : plan.apiitemsLimit} tkns</span>
                <span>📦 {plan.itemsLimit === -1 ? '∞' : plan.itemsLimit} items</span>
              </div>
            </button>
          ))}
          {plans.length === 0 && <p className="text-gray-400 text-center py-4">No active plans available</p>}
        </div>
      </Modal>

      {/* ── Reset Password Modal ── */}
      <Modal isOpen={!!resetPwdCustomer} onClose={() => setResetPwdCustomer(null)} title="Reset Customer Password">
        {resetPwdCustomer && (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm">
              <div className="font-medium text-gray-800">{resetPwdCustomer.name}</div>
              <div className="text-xs text-gray-500">{resetPwdCustomer.email}</div>
            </div>

            <div className="space-y-2">
              <label className="flex items-start gap-2 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                <input type="radio" name="resetMode" value="auto" checked={resetMode === 'auto'}
                  onChange={() => setResetMode('auto')} className="mt-0.5" />
                <div>
                  <div className="text-sm font-medium">Auto-generate password</div>
                  <div className="text-xs text-gray-500">A temporary password will be created and emailed to the customer. They must change it on next login.</div>
                </div>
              </label>
              <label className="flex items-start gap-2 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                <input type="radio" name="resetMode" value="custom" checked={resetMode === 'custom'}
                  onChange={() => setResetMode('custom')} className="mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-medium">Set password manually</div>
                  <div className="text-xs text-gray-500 mb-2">Enter a password for the customer (min 6 characters).</div>
                  {resetMode === 'custom' && (
                    <input type="text" value={resetPwd} onChange={e => setResetPwd(e.target.value)}
                      placeholder="New password" autoFocus
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                  )}
                </div>
              </label>
            </div>

            {generatedPwd && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-amber-700 mb-1">Temporary password (also emailed):</p>
                <p className="font-mono text-sm bg-white border border-amber-200 rounded px-2 py-1 select-all">{generatedPwd}</p>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setResetPwdCustomer(null)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm">
                {generatedPwd ? 'Close' : 'Cancel'}
              </button>
              {!generatedPwd && (
                <button type="submit" disabled={resetting}
                  className="flex-1 bg-amber-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-700 disabled:opacity-60">
                  {resetting ? 'Resetting...' : 'Reset Password'}
                </button>
              )}
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default Customers;
